import axios from 'axios'

// Define the API base URL
const API_URL = process.env.VUE_APP_API_URL || 'http://127.0.0.1:8000'

// Create axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

// Add request interceptor to add auth token
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    console.log('API Request:', config.url, 'Token exists:', !!token)

    if (token && token !== 'undefined' && token !== 'null') {
      config.headers.Authorization = `Bearer ${token}`
      console.log('Added Authorization header with Bearer token')
    } else {
      console.warn('No valid token found in localStorage!')
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// Add response interceptor to handle common errors
apiClient.interceptors.response.use(
  response => {
    return response
  },
  error => {
    // Handle 401 Unauthorized errors (token expired)
    if (error.response && error.response.status === 401) {
      // Check if this request should ignore auto-logout
      if (!error.config.skipAuthRedirect) {
        // Clear local storage and redirect to login
        localStorage.removeItem('token')
        localStorage.removeItem('user')

        // Remove token from axios headers
        delete axios.defaults.headers.common['Authorization']

        // Redirect to login page
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  login(credentials) {
    return apiClient.post('/api/login', credentials)
  },

  register(userData) {
    // Add password_confirmation field if it doesn't exist
    if (userData.password && !userData.password_confirmation) {
      userData.password_confirmation = userData.password
    }
    return apiClient.post('/api/register', userData)
  },

  logout() {
    return apiClient.post('/api/logout')
  },

  updateProfile(userData) {
    return apiClient.put('/api/user', userData)
  },

  getCurrentUser() {
    return apiClient.get('/api/user')
  },

  requestPasswordReset(email) {
    return apiClient.post('/api/reset-password', { email })
  },

  verifyPasswordReset(data) {
    return apiClient.post('/api/reset-password/verify', data)
  }
}

// Subscription API
export const subscriptionAPI = {
  getCurrentSubscription() {
    return apiClient.get('/api/subscriptions/current')
  },

  subscribeToPlan(planId, paymentMethodId) {
    return apiClient.post('/api/subscriptions/subscribe', { planId, paymentMethodId })
  },

  cancelSubscription() {
    return apiClient.post('/api/subscriptions/cancel')
  },

  changePlan(planId) {
    return apiClient.post('/api/subscriptions/change-plan', { planId })
  },

  getBillingHistory() {
    return apiClient.get('/api/subscriptions/billing-history')
  }
}

// Blog Posts API
export const blogPostsAPI = {
  getAllPosts() {
    return apiClient.get('/api/blog-posts')
  },

  getPostById(postId) {
    return apiClient.get(`/api/blog-posts/${postId}`)
  },

  scheduleBlogPost(postData) {
    return apiClient.post('/api/blog-posts/schedule', postData)
  },

  cancelBlogPost(postId) {
    return apiClient.post(`/api/blog-posts/${postId}/cancel`)
  }
}

// Integrations API
export const integrationsAPI = {
  getAllIntegrations() {
    const token = localStorage.getItem('token')
    console.log('GET Integrations - Token exists:', !!token)

    // Create custom headers with authentication token
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }

    // Explicitly add the authorization header
    if (token && token !== 'undefined' && token !== 'null') {
      headers.Authorization = `Bearer ${token}`
      console.log('Explicitly added Authorization header for get integrations request')
    }

    return apiClient.get('/api/integrations', {
      headers,
      skipAuthRedirect: true // Skip auto redirect for this request
    })
  },

  addIntegration(integrationData) {
    const token = localStorage.getItem('token')
    console.log('ADD Integration - Token exists:', !!token)

    // Create custom headers with authentication token
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }

    // Explicitly add the authorization header
    if (token && token !== 'undefined' && token !== 'null') {
      headers.Authorization = `Bearer ${token}`
      console.log('Explicitly added Authorization header for add integration request')
    }

    return apiClient.post(
      '/api/integrations',
      integrationData,
      { headers }
    )
  },

  updateIntegration(integrationId, integrationData) {
    const token = localStorage.getItem('token')
    console.log('UPDATE Integration - Token exists:', !!token)

    // Create custom headers with authentication token
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }

    // Explicitly add the authorization header
    if (token && token !== 'undefined' && token !== 'null') {
      headers.Authorization = `Bearer ${token}`
      console.log('Explicitly added Authorization header for update request')
    }

    return apiClient.put(
      `/api/integrations/${integrationId}`,
      integrationData,
      { headers }
    )
  },

  deleteIntegration(integrationId) {
    const token = localStorage.getItem('token')
    console.log('DELETE Integration - Token exists:', !!token)

    // Create custom headers with authentication token
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }

    // Explicitly add the authorization header
    if (token && token !== 'undefined' && token !== 'null') {
      headers.Authorization = `Bearer ${token}`
      console.log('Explicitly added Authorization header for delete integration request')
    }

    return apiClient.delete(
      `/api/integrations/${integrationId}`,
      { headers }
    )
  },

  testIntegration(integrationId) {
    const token = localStorage.getItem('token')
    console.log('TEST Integration - Token exists:', !!token)

    // Create custom headers with authentication token
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }

    // Explicitly add the authorization header
    if (token && token !== 'undefined' && token !== 'null') {
      headers.Authorization = `Bearer ${token}`
      console.log('Explicitly added Authorization header for test integration request')
    }

    // Create a cancellation token source
    const source = axios.CancelToken.source()

    const request = apiClient.post(
      `/api/integrations/${integrationId}/test`,
      {},
      {
        headers,
        cancelToken: source.token
      }
    )

    // Attach the cancel method to the promise
    request.cancel = () => {
      source.cancel('Operation canceled by the user.')
    }

    return request
  },

  getCategories(integrationId, vocabulary_id = null) {
    const token = localStorage.getItem('token')

    // Create custom headers with authentication token
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }

    // Explicitly add the authorization header
    if (token && token !== 'undefined' && token !== 'null') {
      headers.Authorization = `Bearer ${token}`
    }

    // Build query params if vocabulary_id provided
    const params = vocabulary_id ? { vocabulary_id } : {}

    return apiClient.get(
      `/api/integrations/${integrationId}/categories`,
      { headers, params }
    )
  },

  getVocabularies(integrationId) {
    const token = localStorage.getItem('token')

    // Create custom headers with authentication token
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }

    // Explicitly add the authorization header
    if (token && token !== 'undefined' && token !== 'null') {
      headers.Authorization = `Bearer ${token}`
    }

    return apiClient.get(
      `/api/integrations/${integrationId}/vocabularies`,
      { headers }
    )
  },

  retryPlanningExecution(executionId) {
    const token = localStorage.getItem('token')
    console.log('RETRY Planning Execution - Token exists:', !!token)

    // Create custom headers with authentication token
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }

    // Explicitly add the authorization header
    if (token && token !== 'undefined' && token !== 'null') {
      headers.Authorization = `Bearer ${token}`
      console.log('Explicitly added Authorization header for retry execution request')
    }

    return apiClient.post(
      `/api/planning-executions/${executionId}/retry`,
      {},
      { headers }
    )
  }
}

// Admin API
export const adminAPI = {
  masqueradeUser(userId) {
    return apiClient.post(`/api/admin/users/${userId}/masquerade`)
  },

  stopMasquerade() {
    return apiClient.post('/api/admin/masquerade/stop')
  }
}

export default {
  auth: authAPI,
  subscription: subscriptionAPI,
  blogPosts: blogPostsAPI,
  integrations: integrationsAPI,
  admin: adminAPI
} 